import { useEffect, useState } from 'react'
import { client, newIdempotencyKey } from '../lib/api'
import {
  PHOTO_REQUEST_CATEGORY_OPTIONS,
  PHOTO_REQUEST_DISPUTE_OPTIONS,
  PHOTO_REQUEST_TAGLINE,
  categoryLabel,
  statusLabel,
  type PhotoRequestCategory,
  type PhotoRequestDisputeReason,
} from '../../shared/photo-requests'

export type PhotoRequestRow = {
  id: string
  conversationId: string
  requesterId: string
  creatorId: string
  category: PhotoRequestCategory
  promptNotes?: string | null
  amountCredits: number
  counterAmountCredits?: number | null
  status: string
  blockReason?: string | null
  expiresAt?: string | null
  deliveredAt?: string | null
  reviewDeadlineAt?: string | null
  createdAt: string
}

export function PhotoRequestPanel({
  meId,
  peerId,
  conversationId,
  notify,
  onSent,
}: {
  meId: string
  peerId: string
  conversationId: string | null
  notify: (msg: string) => void
  onSent?: () => void
}) {
  const [open, setOpen] = useState(false)
  const [composer, setComposer] = useState<{
    enabled: boolean
    allowedCategories: PhotoRequestCategory[]
    minAmountCredits: number
  } | null>(null)
  const [requests, setRequests] = useState<PhotoRequestRow[]>([])
  const [category, setCategory] = useState<PhotoRequestCategory>('selfie')
  const [promptNotes, setPromptNotes] = useState('')
  const [amount, setAmount] = useState(25)
  const [busy, setBusy] = useState(false)
  const [fulfillFile, setFulfillFile] = useState<File | null>(null)

  const active = requests.find((r) => !['completed', 'declined', 'expired', 'refunded', 'blocked'].includes(r.status))
  const canSendNew = composer?.enabled && !active

  const reload = async () => {
    if (!conversationId) return
    const r = await client.photoRequestsForConversation(conversationId)
    setRequests(r.requests as PhotoRequestRow[])
  }

  useEffect(() => {
    void client
      .photoRequestComposerSettings(peerId)
      .then((r) => {
        if (r.enabled && r.settings) {
          setComposer({
            enabled: true,
            allowedCategories: r.settings.allowedCategories,
            minAmountCredits: r.settings.minAmountCredits,
          })
          setAmount(r.settings.minAmountCredits)
          setCategory(r.settings.allowedCategories[0] ?? 'selfie')
        } else {
          setComposer({ enabled: false, allowedCategories: [], minAmountCredits: 25 })
        }
      })
      .catch(() => setComposer(null))
  }, [peerId])

  useEffect(() => {
    if (conversationId) void reload().catch(() => undefined)
  }, [conversationId])

  const sendRequest = async () => {
    setBusy(true)
    try {
      const res = await client.createPhotoRequest({
        creatorId: peerId,
        category,
        promptNotes: promptNotes.trim() || undefined,
        amountCredits: amount,
        idempotencyKey: newIdempotencyKey('phreq'),
      })
      notify('Photo request sent — credits held in escrow')
      setOpen(false)
      setPromptNotes('')
      if (res.conversationId) await reload()
      onSent?.()
    } catch (e) {
      notify((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const respond = async (action: 'accept' | 'decline' | 'counter', counterAmountCredits?: number) => {
    if (!active) return
    setBusy(true)
    try {
      await client.respondPhotoRequest(active.id, { action, counterAmountCredits })
      notify(action === 'decline' ? 'Declined — requester refunded' : action === 'counter' ? 'Counter sent' : 'Accepted — upload your photo')
      await reload()
    } catch (e) {
      notify((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const acceptCounter = async () => {
    if (!active || active.status !== 'countered') return
    setBusy(true)
    try {
      await client.acceptPhotoRequestCounter(active.id)
      notify('Counter accepted')
      await reload()
    } catch (e) {
      notify((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const deliver = async () => {
    if (!active || !fulfillFile) return
    setBusy(true)
    try {
      await client.deliverPhotoRequest(active.id, fulfillFile)
      notify('Photo delivered — requester has 24h to review')
      setFulfillFile(null)
      await reload()
    } catch (e) {
      notify((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const confirm = async () => {
    if (!active) return
    setBusy(true)
    try {
      await client.confirmPhotoRequest(active.id)
      notify('Confirmed — payout released')
      await reload()
    } catch (e) {
      notify((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const dispute = async (reason: PhotoRequestDisputeReason) => {
    if (!active) return
    setBusy(true)
    try {
      await client.disputePhotoRequest(active.id, { reason })
      notify('Dispute submitted for review')
      await reload()
    } catch (e) {
      notify((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  if (!composer?.enabled && !active) return null

  return (
    <div className="photo-request-panel">
      {active && (
        <div className="photo-request-active card-lite">
          <strong>{statusLabel(active.status as never)}</strong>
          <p>
            {categoryLabel(active.category)}
            {active.promptNotes ? ` — ${active.promptNotes}` : ''} · {active.amountCredits} credits
            {active.counterAmountCredits ? ` (counter: ${active.counterAmountCredits})` : ''}
          </p>

          {active.creatorId === meId && active.status === 'held' && (
            <div className="photo-request-actions">
              <button type="button" className="button primary" disabled={busy} onClick={() => respond('accept')}>
                Accept
              </button>
              <button type="button" className="button secondary" disabled={busy} onClick={() => respond('decline')}>
                Decline
              </button>
              <button
                type="button"
                className="button secondary"
                disabled={busy}
                onClick={() => respond('counter', active.amountCredits + 10)}
              >
                Counter +10
              </button>
            </div>
          )}

          {active.requesterId === meId && active.status === 'countered' && (
            <button type="button" className="button primary" disabled={busy} onClick={acceptCounter}>
              Accept counter ({active.counterAmountCredits} credits)
            </button>
          )}

          {active.creatorId === meId && active.status === 'accepted' && (
            <div className="field">
              <label>Upload fulfillment photo</label>
              <input type="file" accept="image/*" onChange={(e) => setFulfillFile(e.target.files?.[0] ?? null)} />
              <button type="button" className="button primary" disabled={busy || !fulfillFile} onClick={deliver}>
                Deliver photo
              </button>
            </div>
          )}

          {active.requesterId === meId && active.status === 'delivered' && (
            <div className="photo-request-actions">
              <button type="button" className="button primary" disabled={busy} onClick={confirm}>
                Confirm delivery
              </button>
              <details>
                <summary>Report an issue (objective only)</summary>
                <div className="chip-row">
                  {PHOTO_REQUEST_DISPUTE_OPTIONS.map((opt) => (
                    <button key={opt.value} type="button" className="chip" disabled={busy} onClick={() => dispute(opt.value)}>
                      {opt.label}
                    </button>
                  ))}
                </div>
                <p className="field-hint">“I didn’t like the photo” is not an automatic refund.</p>
              </details>
            </div>
          )}
        </div>
      )}

      {composer?.enabled && canSendNew && (
        <>
          {!open ? (
            <button type="button" className="button secondary photo-request-open" onClick={() => setOpen(true)}>
              Request a custom photo
            </button>
          ) : (
            <div className="photo-request-composer card-lite">
              <h3>Custom photo request</h3>
              <p className="field-hint">{PHOTO_REQUEST_TAGLINE}</p>
              <div className="field">
                <label>Category</label>
                <div className="chip-row">
                  {PHOTO_REQUEST_CATEGORY_OPTIONS.filter((o) => composer.allowedCategories.includes(o.value)).map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`chip ${category === opt.value ? 'selected' : ''}`}
                      onClick={() => setCategory(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="field">
                <label>Notes (optional)</label>
                <input
                  className="input"
                  value={promptNotes}
                  onChange={(e) => setPromptNotes(e.target.value)}
                  placeholder="e.g. blue sweater, coffee shop vibe"
                />
              </div>
              <div className="field">
                <label>Offer (credits)</label>
                <input
                  className="input"
                  type="number"
                  min={composer.minAmountCredits}
                  max={500}
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                />
                <span className="field-hint">Minimum {composer.minAmountCredits} · held in escrow until delivery</span>
              </div>
              <div className="photo-request-actions">
                <button type="button" className="button primary" disabled={busy} onClick={sendRequest}>
                  Send request
                </button>
                <button type="button" className="button secondary" onClick={() => setOpen(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
